import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import Cookies from "js-cookie";

const SecondaryChatHistory = () => {
  const { phoneNumber } = useParams();

  // Resolve agent login: from param → cookie → fallback (optional)
  let phone =
    phoneNumber && phoneNumber !== undefined && phoneNumber !== "0"
      ? phoneNumber
      : Cookies.get("phone") || null;

  console.log("Customer phone", phone);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startCount, setStartCount] = useState(0);
  const [endCount, setEndCount] = useState(20);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const chatBoxRef = useRef(null);
  const hasFetchedMessages = useRef(false);

  // Group messages by date and sort by ID
  const groupMessagesByDate = (messages) => {
    // Ensure messages is an array
    if (!Array.isArray(messages)) {
      console.error("Expected messages to be an array but got:", messages);
      return {}; // Return empty object in case of invalid data
    }

    const sortedMessages = [...messages].sort((a, b) => a.id - b.id);

    // Group the messages by date
    return sortedMessages.reduce((groups, message) => {
      if (!message.dateAndTime) {
        console.error("Invalid dateAndTime field:", message);
        return groups; // Skip message if dateAndTime is missing or invalid
      }

      // Parse the date
      const date = new Date(message.dateAndTime).toLocaleDateString();

      // Check if the date was valid
      if (isNaN(new Date(date).getTime())) {
        console.error("Invalid date format:", message.dateAndTime);
        return groups; // Skip message if date format is invalid
      }

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };

  // Function to get a cookie by name
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const phoneCookie =
        phoneNumber && phoneNumber !== "0"
          ? phoneNumber
          : Cookies.get("phone") || null;

      if (phoneCookie) {
        console.log("Phone from cookie:", phoneCookie);
        phone = phoneCookie;
      } else {
        console.log("Phone cookie not found.");
      }
    }, 2000); // every 10 seconds

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  // Save login data
  const saveLogin = async () => {
    setLoading(true);
    const agentIdFromCookie = getCookie("AgentID");

    if (!agentIdFromCookie) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/sms/conversation/${phone}/${agentIdFromCookie}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone,
            login: agentIdFromCookie,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save login data");
      }

      // const data = await response.json();
      // setMessages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateSmsDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/sms/updateSmsDetails?phone=${phone}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save login data");
      }

      // const data = await response.json();
      // setMessages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = (isScrollToTop = false) => {
    if (loading || !hasMoreMessages) return;

    setLoading(true);

    const chatBox = chatBoxRef.current;
    const previousScrollHeight = chatBox ? chatBox.scrollHeight : 0;

    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/sms/messages/${phone}?startCount=${startCount}&endCount=${endCount}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch messages");
        }
        return response.json();
      })
      .then((data) => {
        // Ensure 'data' is an array before processing
        if (!Array.isArray(data)) {
          console.error("Expected an array of messages, but got:", data);
          return;
        }

        if (data.length === 0) {
          setHasMoreMessages(false);
        }

        setMessages((prevMessages) => {
          const allMessages = isScrollToTop
            ? [...data, ...prevMessages]
            : [...prevMessages, ...data];

          const uniqueMessages = allMessages.filter(
            (message, index, self) =>
              index === self.findIndex((m) => m.id === message.id)
          );

          return uniqueMessages;
        });
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
        setStartCount(startCount + 20);
        setEndCount(endCount + 20);

        // Restore the scroll position
        setTimeout(() => {
          if (chatBox) {
            chatBox.scrollTop =
              chatBox.scrollTop + (chatBox.scrollHeight - previousScrollHeight);
          }
        }, 0); // Adjust timing if necessary
      });
  };

  // Handle scroll event
  const handleScroll = useCallback(
    debounce(() => {
      if (chatBoxRef.current?.scrollTop === 0 && hasMoreMessages && !loading) {
        fetchMessages(true);
      }
    }, 300),
    [hasMoreMessages, loading]
  );

  // Attach and detach scroll event
  useEffect(() => {
    const chatBox = chatBoxRef.current;
    if (chatBox) {
      chatBox.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatBox) {
        chatBox.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasFetchedMessages.current && phone && phone.length >= 10) {
      fetchMessages();
      saveLogin();
      updateSmsDetails();
      hasFetchedMessages.current = true;
      scrollToBottom(); // Scroll to bottom on first render
    }
  }, []);

  const scrollToBottomConditionally = () => {
    if (chatBoxRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // Adjust threshold if needed
      if (isAtBottom) {
        scrollToBottom();
      }
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottomConditionally();
    }
  }, [messages]);

  // Scroll to bottom on new messages
  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const groupedMessages = groupMessagesByDate(messages);

  console.log(groupedMessages);

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        maxWidth: "100%",
        margin: "0 auto",
        padding: "10px",
      }}
    >
      <div
        style={{
          padding: "9px",
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#5c5eee",
          borderRadius: "8px 8px 0 0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <h2
          style={{
            margin: "0",
            fontSize: "16px",
            fontWeight: "500",
            color: "white",
          }}
        >
          Chat History for {phone}
        </h2>
      </div>
      <div
        style={{
          height: "280px",
          overflowY: "auto",
          border: "1px solid #e0e0e0",
          background: "linear-gradient(135deg, #f8bbd0 0%, #bbdefb 100%)",
          borderRadius: "0 0 8px 8px",
          padding: "10px",
          scrollbarWidth: "thin" /* Firefox */,
          scrollbarColor: "rgba(155, 155, 155, 0.5) transparent" /* Firefox */,
          msOverflowStyle: "-ms-autohiding-scrollbar" /* IE and Edge */,
        }}
        ref={chatBoxRef}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
    /* Thin scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
      width: 6px;
      background-color: transparent;
    }
    
    ::-webkit-scrollbar-thumb {
      background-color: rgba(155, 155, 155, 0.5);
      border-radius: 4px;
    }
    
    ::-webkit-scrollbar-track {
      background-color: transparent;
    }
    
    /* Auto-hiding scrollbar */
    ::-webkit-scrollbar-thumb {
      visibility: hidden;
    }
    
    *:hover::-webkit-scrollbar-thumb,
    *:focus::-webkit-scrollbar-thumb,
    *:active::-webkit-scrollbar-thumb {
      visibility: visible;
    }
  `,
          }}
        />

        {Object.entries(groupedMessages).map(([date, messagesForDate]) => (
          <div key={date} style={{ marginBottom: "20px" }}>
            <div
              style={{
                padding: "8px 0",
                textAlign: "center",
                color: "#666",
                fontSize: "13px",
                fontWeight: "500",
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                margin: "10px 0",
              }}
            >
              {date}
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {messagesForDate.map((message) => (
                <div
                  key={message.id}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    maxWidth: "80%",
                    wordBreak: "break-word",
                    background:
                      message.sender === "Agent"
                        ? "linear-gradient(135deg, #e1f5fe 0%, #bbdefb 100%)"
                        : "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                    alignSelf:
                      message.sender === "Agent" ? "flex-start" : "flex-end",
                    marginLeft: message.sender === "Agent" ? "0" : "auto",
                    marginRight: message.sender === "Agent" ? "auto" : "0",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  {message.message}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!hasMoreMessages && messages.length > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "10px",
            color: "#666",
            fontSize: "13px",
            backgroundColor: "#f5f7fa",
            borderRadius: "0 0 8px 8px",
            marginTop: "5px",
          }}
        >
          No more messages to load
        </div>
      )}

      {loading && messages.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "15px",
            backgroundColor: "#f5f7fa",
            borderRadius: "8px",
            margin: "10px 0",
          }}
        >
          Loading...
        </div>
      )}

      {error && (
        <div
          style={{
            color: "#e53935",
            padding: "10px",
            backgroundColor: "#ffebee",
            borderRadius: "8px",
            border: "1px solid #ffcdd2",
            margin: "10px 0",
          }}
        >
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default SecondaryChatHistory;
